/**
 * Componente per proteggere le route che richiedono autenticazione
 * Reindirizza al login se l'utente non è autenticato
 */
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSupabaseAuth } from "../context/SupabaseAuthContext";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: "admin" | "editor" | "viewer";
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
}) => {
  const { isAuthenticated, user, loading } = useSupabaseAuth();
  const location = useLocation();

  // Mostra loader mentre stiamo controllando l'autenticazione
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifica autenticazione...</p>
        </div>
      </div>
    );
  }

  // Se non è autenticato, reindirizza al login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Se è richiesto un ruolo specifico, controlla i permessi
  if (requiredRole && user) {
    const hasPermission = checkRolePermission(user.ruolo, requiredRole);

    if (!hasPermission) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="max-w-md w-full mx-auto">
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-red-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Accesso Negato
              </h3>
              <p className="text-gray-600 mb-4">
                Non hai i permessi necessari per accedere a questa sezione.
              </p>
              <div className="text-sm text-gray-500 mb-4">
                <p>
                  Ruolo richiesto:{" "}
                  <span className="font-medium">{requiredRole}</span>
                </p>
                <p>
                  Il tuo ruolo:{" "}
                  <span className="font-medium">{user.ruolo}</span>
                </p>
              </div>
              <button
                onClick={() => window.history.back()}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition duration-200"
              >
                Torna Indietro
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  // Se tutto è ok, renderizza i children
  return <>{children}</>;
};

/**
 * Funzione per controllare se un ruolo ha i permessi necessari
 */
function checkRolePermission(
  userRole: "admin" | "editor" | "viewer",
  requiredRole: "admin" | "editor" | "viewer"
): boolean {
  const roleHierarchy = {
    admin: 3,
    editor: 2,
    viewer: 1,
  };

  return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
}

export default ProtectedRoute;
